/**
 * useElementWidth — an element's rendered CSS pixel width, kept current.
 *
 * WHY THE LINE CHARTS NEED THIS. They are hand-rolled SVG drawn into a fixed
 * `viewBox` and stretched with `w-full`, which means EVERY length inside the
 * SVG — the `text-nano` axis labels, `strokeWidth`, the marker radii — is in
 * viewBox units and is multiplied by `renderedWidth / viewBoxWidth`. With a
 * fixed 480-unit box that factor is ~1.0 in the Dashboard tab's two-column grid
 * and ~2.1-2.5 in the Search Progress tab's full-width stack, so the same
 * component rendered the same 9px label at 9px on one tab and ~20px on the
 * other. That was reported as "the font size is much bigger on the other tab"
 * (Trello #711 CL3) and it is not a styling difference at all.
 *
 * Setting the viewBox width to the MEASURED width makes one viewBox unit equal
 * one CSS pixel, so a declared 9px renders at 9px at any container size and the
 * two tabs agree by construction rather than by a tuned constant.
 *
 * IT TAKES A CALLBACK REF, AND THAT IS THE WHOLE DESIGN. The first version took
 * a `RefObject` and depended on `[ref]` — an object whose identity never
 * changes, so the effect ran exactly once, on mount, and never again. Every
 * chart early-returns its empty state BEFORE rendering the ref'd surface, so a
 * chart whose FIRST render is empty never got observed at all: widen the Search
 * Progress range back out and the surface mounts into a hook that has already
 * given up, leaving a full-width panel at the 480-unit fallback — the exact
 * oversized-label defect this hook exists to fix, reachable from a shared URL.
 * The reverse order failed too: non-empty → empty → non-empty left the observer
 * watching a detached node with the width frozen.
 *
 * A callback ref makes the ELEMENT the dependency, which is what the effect
 * actually depends on. Mount, unmount and replacement all re-run it. Found by
 * two independent reviewers on PR #752; the six tests below could not catch it
 * because their probe always rendered the ref'd node on the first pass, so one
 * of them now renders it conditionally.
 *
 * THE FALLBACK IS LOAD-BEARING. A zero measurement means the element is not
 * laid out — hidden, detached, or running under happy-dom, which reports every
 * rect as zero and is how every chart unit test in this repo runs. A zero-width
 * viewBox collapses the whole chart, so zero reads as "not measured" and returns
 * the caller's fallback. `useNearestPoint` already makes the same call for the
 * same reason.
 */

import { type RefCallback, useCallback, useLayoutEffect, useState } from "react";

/**
 * The viewBox width used when the container cannot be measured.
 *
 * ONE declaration for all three line charts. It was three identical constants
 * with three copies of the rationale, which is three places for the reasoning to
 * drift (PR #752, Agent A §7). The value is arbitrary in the way a fallback is —
 * what matters is that it is non-zero, because a zero-width viewBox collapses
 * the chart entirely.
 */
export const FALLBACK_CHART_WIDTH = 480;

export interface MeasuredWidth {
  /** Spread onto the element to measure: `ref={measured.ref}`. */
  ref: RefCallback<HTMLElement>;
  /** The measured width, or `fallback` until a non-zero one is observed. */
  width: number;
}

/**
 * Watch an element's width. Returns `fallback` until a non-zero one is observed.
 *
 * `useLayoutEffect`, not `useEffect`: an effect runs AFTER paint, so every mount
 * and every tab switch would show one frame at the fallback width — a visible
 * flash of exactly the oversized labels this hook removes. There is no SSR in
 * this app, so the usual reason to prefer `useEffect` does not apply.
 *
 * `ResizeObserver` rather than a one-shot measurement: these charts sit in a
 * responsive grid and inside a tab that mounts them at a width the layout has
 * not settled on yet, so a single read would pin the wrong number for the life
 * of the component.
 */
export function useElementWidth(fallback: number): MeasuredWidth {
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [width, setWidth] = useState(0);

  // Stable identity so passing it as `ref` does not detach and re-attach on
  // every render — React calls a callback ref again whenever its identity
  // changes, which would reset `element` and restart the observer each time.
  const ref = useCallback<RefCallback<HTMLElement>>((node) => setElement(node), []);

  useLayoutEffect(() => {
    if (!element || typeof ResizeObserver === "undefined") return;

    const apply = (next: number) => {
      // `Math.round` IS WHAT STOPS A FEEDBACK LOOP. That is not obvious, so:
      // `width` feeds the viewBox; the SVG is `w-full` with no explicit height,
      // so its rendered HEIGHT is a function of the width — and a default
      // content-box observation reports height changes too. The observer
      // therefore fires again after every width change. Rounding makes that
      // second observation yield the IDENTICAL number, `useState` bails on
      // `Object.is`, and the loop terminates. Without it, a sub-pixel difference
      // each time would keep it going.
      //
      // (The `rounded === current ? current : rounded` below is belt-and-braces
      // and could be `setWidth(Math.round(next))` — `useState` already bails on
      // an equal value. It is kept only because it makes the bail-out visible at
      // the call site. The rounding is the load-bearing half; PR #752, Agent C
      // caught this sign being nailed to the wrong line.)
      setWidth((current) => {
        const rounded = Math.round(next);
        return rounded === current ? current : rounded;
      });
    };

    apply(element.getBoundingClientRect().width);
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  return { ref, width: width > 0 ? width : fallback };
}
